import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "メルカリ「画像をアップロードできませんでした」重い画像を出品する方法",
	description:
		"メルカリで「画像をアップロードできませんでした」が表示される原因（ファイルサイズ上限・形式）と、大きすぎる画像を圧縮して出品できるようにする手順を解説。",
	publishDate: "2026-03-16",
	slug: "mercari-image-upload-failed",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function MercariImageUploadFailed() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section className="mb-8">
						<p className="text-gray-700 leading-relaxed">
							「画像をアップロードできませんでした」——せっかく撮影した写真なのに、このエラーが出てしまうと困りますよね。
							特に最近のスマートフォンは高画質で撮影されるため、ファイルサイズが大きくなりがちです。
							この記事では、エラーの原因と今すぐできる解決方法を解説します。
						</p>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							エラーの原因：ファイルサイズと形式の制限
						</h2>
						<p className="text-gray-700 leading-relaxed mb-4">
							メルカリで画像アップロードに失敗する主な原因は以下の通りです。
						</p>

						<div className="space-y-4">
							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因1: ファイルサイズが大きすぎる
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									メルカリShopsでは1枚あたり8MBを超えるとアップロードエラーになります。
									iPhone 15 Pro
									Maxなどの最新機種では1枚20〜30MBになることもあり、
									そのままでは出品できません。
									また1回のアップロード総量が1GBを超える場合もエラーになります。
								</p>
							</div>

							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因2: 対応していないファイル形式
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									対応形式はJPEG・PNGのみです。
									iPhoneのデフォルト設定ではHEIF（.heic）形式で保存されることがあり、
									この形式はメルカリで使えません。 WebPも非対応です。
								</p>
							</div>

							<div className="bg-red-50 border border-red-200 rounded-xl p-5">
								<h3 className="font-semibold text-red-800 mb-2">
									原因3: ネットワークの問題（一時的なエラー）
								</h3>
								<p className="text-gray-700 text-sm leading-relaxed">
									Wi-Fiが不安定な場合や、アプリのキャッシュが溜まっている場合にも
									「アップロードできませんでした」が表示されます。
									この場合は少し時間をおいてから再試行するか、アプリを再起動してください。
								</p>
							</div>
						</div>

						<div className="bg-gray-50 rounded-xl p-5 mt-4">
							<h3 className="font-semibold text-gray-800 mb-3">
								メルカリの画像要件まとめ
							</h3>
							<table className="w-full text-sm text-gray-700">
								<thead>
									<tr className="border-b">
										<th className="text-left py-2 font-semibold">項目</th>
										<th className="text-left py-2 font-semibold">規格</th>
									</tr>
								</thead>
								<tbody>
									<tr className="border-b">
										<td className="py-2">ファイルサイズ上限</td>
										<td className="py-2">8MB/枚（メルカリShops）</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">対応形式</td>
										<td className="py-2">JPEG、PNG</td>
									</tr>
									<tr className="border-b">
										<td className="py-2">推奨サイズ</td>
										<td className="py-2">720 × 720px（正方形）</td>
									</tr>
									<tr>
										<td className="py-2">最大枚数</td>
										<td className="py-2">1商品あたり最大12枚</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">解決手順</h2>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>ファイル形式をJPEGまたはPNGに変換する</strong>
								<p className="mt-1 ml-5 text-sm">
									iPhoneの場合：「設定 → カメラ → フォーマット →
									互換性優先」に変更すると
									JPEGで撮影されるようになります（撮影前の設定変更が必要）。
									または撮影済みのHEIC画像はCloudConvert等で変換できます。
								</p>
							</li>
							<li>
								<strong>ファイルサイズを8MB以下に圧縮する</strong>
								<p className="mt-1 ml-5 text-sm">
									Squoosh（squoosh.app）を開いてブラウザ上で圧縮します。
									JPEG品質70〜80%で出力すると、ほとんどの場合8MB以下に収まります。
									画質の劣化は肉眼ではほぼわかりません。
								</p>
							</li>
							<li>
								<strong>正方形(720×720px)にリサイズして出品する</strong>
								<p className="mt-1 ml-5 text-sm">
									メルカリでは正方形の画像が一覧表示で見やすく、購買率が上がります。
									リサイズしながら余白を白で埋めると商品が映えます。
								</p>
							</li>
						</ol>
					</section>

					<section className="mb-8">
						<h2 className="text-xl font-semibold mb-4">
							しろぬきやで自動変換する方法
						</h2>
						<p className="text-gray-700 leading-relaxed">
							形式変換・圧縮・リサイズを毎回手動でやるのは手間がかかります。
							<strong>しろぬきや</strong>
							では、画像をブラウザにドロップするだけで
							AI背景除去＋正方形リサイズ（720×720px）＋8MB以下への自動圧縮が完了します。
							画像はブラウザ上で処理されるため、商品情報がサーバーに送られる心配もありません。
						</p>
					</section>

					<CtaBlock />
					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
